import { useEffect, useState } from 'react';

import type { ReactNode } from 'react';
import { Bookmark, GeoAlt } from 'react-bootstrap-icons';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { IconTitle } from '../../../IconTitle';
import type { CellComponent, RowDefinition, SectionComponent } from '../../types';

import { RowComponent } from './row';

export const CardSection = ({
  def,
  renderComponent
}: {
  def: SectionComponent;
  renderComponent: (definition: CellComponent) => ReactNode;
}) => {
  const { cells, name } = def;
  const { control, getValues } = useFormContext();
  const instancesPath = `sections.${def.code}.instances`;
  const { fields, append, remove } = useFieldArray({ control, name: instancesPath });
  const [rows, setRows] = useState<Array<RowDefinition>>([]);
  const isJurisdiction = def.sectionType === 'jurisdiction';
  const minimum = isJurisdiction
    ? fields.length
    : def.sectionType === 'multiple'
      ? (def.effectiveMinInstances ?? def.minInstances ?? 0)
      : 1;
  const maximum = isJurisdiction
    ? minimum
    : def.sectionType === 'multiple'
      ? (def.effectiveMaxInstances ?? def.maxInstances)
      : 1;

  useEffect(() => {
    const newRows = [{ id: `${def.code}-0`, cols: [] }] as Array<RowDefinition>;
    let numberRows = 0;

    cells.forEach((value, index) => {
      if (index !== 0 && index % 2 === 0) {
        const newRow = { id: `${def.code}-${index}`, cols: [] } as RowDefinition;
        newRow.cols.push(value);
        newRows.push(newRow);
        numberRows++;
      } else {
        const actual = newRows[numberRows];
        actual.cols.push(value);
      }
    });

    setRows(newRows);
  }, [cells, def.code]);

  useEffect(() => {
    if (def.readOnly) return;

    // if (!isJurisdiction) {
    //   while (fields.length < minimum) {
    //     append(createSectionInstance(def));
    //   }
    // }
  }, [append, def, fields.length, minimum]);

  if (rows?.length === 0) {
    return <></>;
  }

  return (
    <div className="mb-3">
      <div className="px-4">
        <div className="mb-4">
          <IconTitle
            title={name}
            subtitle={name + ' information'}
            icon={Bookmark}
            iconClassName="text-primary"
            titleClassName="text-dark"
          />
        </div>

        {fields.map((field, index) => {
          const cellsGrid = rows.map((row) => (
            <div key={row.id}>
              <RowComponent
                row={row}
                renderComponent={(cell) =>
                  renderComponent({
                    ...cell,
                    fieldPath: `${instancesPath}.${index}.cells.${cell.code}`
                  })
                }
              />
            </div>
          ));

          if (isJurisdiction) {
            const instanceIdentity =
              getValues(`${instancesPath}.${index}.sectionRowId`) ?? String(index + 1);

            const jurisdiction = def.jurisdictions?.find(
              (candidate) => candidate.id === instanceIdentity
            );

            return (
              <div
                className="card mb-3"
                key={index}
                style={{ borderLeft: `2px solid ${jurisdiction?.color}` }}>
                <div className="card-header header-jurisdiction">
                  <IconTitle
                    title={jurisdiction?.name ?? ''}
                    subtitle={jurisdiction?.id}
                    icon={GeoAlt}
                    iconColor={jurisdiction?.color}
                  />
                </div>
                <div className="card-body">{cellsGrid}</div>
              </div>
            );
          }

          return (
            <div key={field.id}>
              {cellsGrid}
              {def.sectionType === 'multiple' && !def.readOnly && fields.length > minimum && (
                <button type="button" onClick={() => remove(index)}>
                  Remove instance
                </button>
              )}
            </div>
          );
        })}
        {/* {def.sectionType === 'multiple' &&
          !def.readOnly &&
          (!maximum || fields.length < maximum) && (
            <button type="button" onClick={() => append(createSectionInstance(def))}>
              Add instance
            </button>
          )} */}
      </div>
    </div>
  );
};
